<template>
  <div class="analytic-plan-container">
    <!-- Header row with badges and controls -->
    <div class="d-flex align-center mb-2">
      <!-- Left side: Info badges -->
      <div class="badges-container">
        <span class="plan-info-badge">Línea: {{ lineNumber }}</span>
        <span class="plan-info-badge">Cuenta: {{ accountCode || 'FALTA CUENTA' }}</span>
        <span class="plan-info-badge">{{ planLabel }}: {{ planName || 'PLAN SIN NOMBRE' }}</span>
        <span class="plan-info-badge">Debe: {{ debit || '0' }}</span>
        <span class="plan-info-badge">Haber: {{ credit || '0' }}</span>
      </div>

      <v-spacer></v-spacer>

      <!-- Right side: Search and button -->
      <div class="controls-container d-flex align-center">
        <v-text-field
          v-if="hasLevelsDefined"
          v-model="screen_data.filterText"
          @input="loader.applyFilter()"
          prepend-inner-icon="mdi-magnify"
          placeholder="Buscar por nombre o código"
          clearable
          dense
          hide-details
          solo
          flat
          class="search-field mr-2"
          style="max-width: 240px; min-width: 240px;"
        />
        <v-btn
          small
          color="primary"
          class="analytics-btn"
          @click="openLevelSelector"
        >
          Niveles Analíticos
        </v-btn>
      </div>
    </div>

    <!-- Empty state message -->
    <div v-if="!hasVisibleLevels" class="empty-state text-center py-14">
      <div class="empty-state-title">Vista de Analíticas</div>
      <div class="empty-state-subtitle">En esta sección aparecerán las analíticas de los datos que elijas.</div>
    </div>

    <v-simple-table v-else dense class="plan-table" fixed-header
      height="250px">
      <thead>
        <tr>
          <template v-for="(e, idx) in screen_data.maxDepth">
            <th class="text-center" :key="'name-'+idx" width="10%">Nombre</th>
            <th class="text-center" :key="'code-'+idx" width="8%">Codigo</th>
            <th class="text-center" :key="'importe-'+idx" width="8%">Importe</th>
            <th v-if="showPercent" class="text-center" :key="'percent-'+idx" width="8%">%</th>
          </template>
        </tr>
      </thead>
      <tbody>
        <template v-for="(row, rowIdx) in screen_data.rows">
          <tr :key="`row-${rowIdx}`" :class="getRowClass(rowIdx)">
            <template v-for="(level, colIdx) in row.path">
              <td
                :rowspan="screen_data.rowspanMap[rowIdx][colIdx]"
                class="text-center"
                v-show="loader.shouldShowCell(rowIdx, colIdx)"
                :key="`nom-${rowIdx}-${colIdx}`"
              >
                <span v-if="level">{{ level.name }} </span>
              </td>
              <td
                :rowspan="screen_data.rowspanMap[rowIdx][colIdx]"
                class="text-center"
                v-show="loader.shouldShowCell(rowIdx, colIdx)"
                :key="`code-${rowIdx}-${colIdx}`"
              >
                <span v-if="level">{{ level.code }}</span>
              </td>
              <td
                :rowspan="screen_data.rowspanMap[rowIdx][colIdx]"
                :class="['text-center', (level && level.ui_error) ? 'cell-error' : '']"
                v-show="loader.shouldShowCell(rowIdx, colIdx)"
                :key="`amount-${rowIdx}-${colIdx}`"
              >
                <v-text-field
                  v-if="level"
                  :value="level.amount"
                  @input="loader.amount_changed(level,$event)"
                  background-color="rgba(0,0,0,0)"
                  type="number"
                  dense
                  hide-details
                  class="mt-0 pt-0 amount-input"
                ></v-text-field>
              </td>
              <td
                v-if="showPercent"
                :rowspan="screen_data.rowspanMap[rowIdx][colIdx]"
                :class="['text-center', (level && level.ui_error) ? 'cell-error' : '']"
                v-show="loader.shouldShowCell(rowIdx, colIdx)"
                :key="`percent-${rowIdx}-${colIdx}`"
              >
                <v-text-field
                  v-if="level"
                  :value="loader.calculatePercent(level, plan.total_amount)"
                  @input="loader.updatePercentage(level, plan.total_amount, $event)"
                  background-color="rgba(0,0,0,0)"
                  :readonly="level.levels && level.levels.length > 0"
                  type="number"
                  step="0.01"
                  dense
                  hide-details
                  suffix="%"
                  class="mt-0 pt-0 amount-input"
                ></v-text-field>
              </td>
            </template>
          </tr>
        </template>
        <tr>
          <template v-for="(e, colIdx) in screen_data.maxDepth">
            <td :key="'total-name-'+colIdx"></td>
            <td :key="'total-label-'+colIdx" class="text-right font-weight-bold"></td>
            <td :key="'total-amount-'+colIdx" class="text-right font-weight-bold">
              T: {{ getLevelTotal(colIdx).toFixed(2) }} <span class="missing-value">[+{{ getMissingAmount().toFixed(2) }}]</span>
            </td>
            <td v-if="showPercent" :key="'total-percent-'+colIdx" class="text-right font-weight-bold">{{ getTotalPercentage().toFixed(2) }}% <span class="missing-value">[+{{ getMissingPercentage().toFixed(2) }}%]</span></td>
          </template>
        </tr>
      </tbody>
    </v-simple-table>

    <!-- Modal for level selection -->
    <AnalyticLevelSelectorPopup
      v-model="showLevelSelectorModal"
      :plan="plan"
      :showPercent="showPercent"
      :accountCode="accountCode"
      :company="company"
      @apply="onLevelSelectorApply"
    />
  </div>
</template>

<script>
import { SelectAnalyticPlan } from "@/modules/analitic_accounting/application/SelectAnalyticPlan"
import { mapActions } from "vuex"
import AnalyticLevelSelectorPopup from "./AnalyticLevelSelectorPopup.vue"

export default {
  name: 'SelectAnalyticPlan',
  components: {
    AnalyticLevelSelectorPopup
  },
  props: {
    plan: { type: Object, required: true },
    showPercent: { type: Boolean, default: false },
    lineNumber: { type: Number, required: true },
    accountCode: { type: String, default: '' },
    company: { type: Object, default: null },
    planLabel: { type: String, default: 'Plan' },
    planName: { type: String, default: '' },
    debit: { type: [String, Number], default: '0' },
    credit: { type: [String, Number], default: '0' },
  },
  data() {
    return {
      screen_data: {
        maxDepth: 0,
        rowspanMap: [],
        rows: [],
        filterText: "",
      },
      loader: null,
      showLevelSelectorModal: false,
    }
  },
  computed: {
    hasVisibleLevels() {
      // Check if there are any rows to display
      return this.screen_data.rows && this.screen_data.rows.length > 0;
    },
    hasLevelsDefined() {
      // Check if the plan has any levels defined (regardless of filtering)
      return this.plan && this.plan.levels && this.plan.levels.length > 0;
    }
  },
  mounted() {
    this.loader = new SelectAnalyticPlan(this.screen_data, this.setLineAnalyticPlanUdated, this.showPercent)
    this.loader.execute(this.plan)
  },
  methods: {
    ...mapActions({
      setLineAnalyticPlanUdated: "documents/setLineAnalyticPlanUdated"
    }),
    openLevelSelector() {
      this.showLevelSelectorModal = true;
    },
    onLevelSelectorApply() {
      this.loader.refreshRows();
    },
    getRowClass(rowIdx) {
      // Count visible rows to determine odd/even status
      let visibleRowCount = 0;
      for (let i = 0; i < rowIdx; i++) {
        if (this.screen_data.rowspanMap[i] && this.screen_data.rowspanMap[i][0] > 0) {
          visibleRowCount++;
        }
      }
      // If this row is visible, increment the counter
      if (this.screen_data.rowspanMap[rowIdx] && this.screen_data.rowspanMap[rowIdx][0] > 0) {
        visibleRowCount++;
      }
      // Apply odd/even class based on visible rows
      return visibleRowCount % 2 === 1 ? 'visible-row-odd' : 'visible-row-even';
    },
    getLevelTotal() {
      if (!this.screen_data.rows || this.screen_data.rows.length === 0) {
        return 0;
      }

      const seenPaths = new Set();
      let total = 0;

      for (const row of this.screen_data.rows) {
        // search for the deepest leaf node in the entire row
        let leafNode = null;
        let leafLevel = -1;

        // Iterate through the entire row to find the deepest leaf node
        for (let i = 0; i < row.path.length; i++) {
          const level = row.path[i];
          if (level && level.code) {
            const hasChildren = level.levels && level.levels.length > 0;
            if (!hasChildren) {
              leafNode = level;
              leafLevel = i;
            }
          }
        }

        // If we found a leaf node, check if it's unique and add it to the total
        if (leafNode) {
          const pathKey = row.path
            .slice(0, leafLevel + 1)
            .map(l => l ? l.code : 'null')
            .join('|');

          if (!seenPaths.has(pathKey)) {
            seenPaths.add(pathKey);
            const amount = parseFloat(leafNode.amount) || 0;
            total += amount;
          }
        }
      }

      return total;
    },
    getTotalPercentage() {
      // Calculate total percentage by dividing the sum of leaf amounts by plan total_amount
      const levelTotal = this.getLevelTotal();
      if (!this.plan || !this.plan.total_amount || this.plan.total_amount === 0) {
        return 0;
      }
      const percent = (Math.abs(levelTotal) / Math.abs(this.plan.total_amount)) * 100;
      return Math.round(percent * 100) / 100; // Round to 2 decimals
    },
    getMissingAmount() {
      // Calculate the amount still missing to complete the total amount of the line
      const levelTotal = this.getLevelTotal();
      if (!this.plan || !this.plan.total_amount) {
        return 0;
      }
      const missing = this.plan.total_amount - levelTotal;
      return missing;
    },
    getMissingPercentage() {
      // Calculate the percentage still missing to complete 100%
      const currentPercentage = this.getTotalPercentage();
      const missing = 100 - currentPercentage;
      return missing;
    }
  }
}
</script>

<style lang="scss" scoped>
.analytic-plan-container {
  margin-left: -1vw;
  margin-right: -1vw;
}

.missing-value {
  font-weight: bold;
  opacity: 0.9;
}

.badges-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.plan-info-badge {
  background-color: var(--v-primaryLighter-base);
  color: var(--v-primaryDark-base);
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  height: 40px;
  display: inline-flex;
  align-items: center;
}

.controls-container {
  flex-shrink: 0;
}

/* Analytics button styles */
.analytics-btn {
  width: 149px !important;
  height: 40px !important;
  border-radius: 8px !important;
  text-transform: none !important;
  padding-top: 7px !important;
  padding-right: 8px !important;
  padding-bottom: 8px !important;
  padding-left: 8px !important;
}

/* Empty state styles */
.empty-state {
  padding: 60px 20px;
}

.empty-state-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--v-primaryDark-base);
  margin-bottom: 12px;
}

.empty-state-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: #6D6D6D;
}

.primary-background {
  background-color: var(--v-primary-base);
}
.primary-border {
  border-color: var(--v-primary-base) !important;
}
.app-confirm-modal__body,
.app-confirm-modal__question {
  font-family: 'Roboto';
  font-style: normal;
  font-size: 20px;
  line-height: 23px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: bold;
}
.app-confirm-modal__body {
  font-weight: 400;
}

.plan-table {
  margin-top: 16px;
  border-radius: 2px;
  overflow: hidden;
  width: 100%;
  table-layout: auto;
}

.plan-table td {
  padding: 2px 4px !important;
  height: 32px !important;
  vertical-align: middle !important;
  font-size: 12px !important;
  border: 1px solid #B0B0B0;
  border-collapse: collapse;
  outline: none !important;
}

.plan-table tbody tr.visible-row-odd td {
  background-color: #EBEAEB !important;
}

/* Error cell background - must override visible-row-odd */
.plan-table tbody tr td.cell-error {
  background-color: #ff9898 !important;
}

.plan-table th {
  font-size: 12px;
  height: 32px !important;
  text-align: center !important;
  vertical-align: middle !important;
  white-space: nowrap !important;
  background-color: var(--v-primaryLighter-base) !important;
  color: var(--v-primaryDark-base) !important;
  font-weight: bold !important;
}

.plan-table td .v-input input,
.plan-table td span {
  color: #6D6D6D !important;
}

.plan-table td .v-input {
  margin: 0 !important;
  width: 100% !important;
  border-bottom: none !important;
  border-radius: 0 !important;
}

.plan-table td .v-text-field::before,
.plan-table td .v-text-field::after {
  border-bottom: none !important;
}

.plan-table td .v-input__slot::before,
.plan-table td .v-input__slot::after {
  border: none !important;
}

.plan-table td .v-text-field .v-input__slot {
  border: none !important;
  box-shadow: none !important;
}

.plan-table td .v-text-field fieldset {
  border: none !important;
}

.plan-table td .v-text-field .v-input__control {
  border: none !important;
}

.plan-table td .v-text-field input {
  font-size: 12px !important;
  height: 28px !important;
}

.plan-table input[type="number"] {
  color: #6D6D6D !important;
}

.plan-table td.hovered-parent {
  background-color: #ede6f7 !important;
}

.plan-table td.importe-col {
  width: 20px !important; /* Adjust as needed */
  min-width: 40px !important;
  max-width: 80px !important;
  padding-left: 2px !important;
  padding-right: 2px !important;
}

.input-error-bg input {
  background-color: #ffe6e6 !important; /* Rojo suave */
}

.btn-active-save-analityc-plan {
  background-color: var(--v-primary-base);
}
.btn-active-save-analityc-plan .v-icon {
  color: white !important;
}

/* Search field styles - matching AnalyticLevelSelectorPopup */
::v-deep .search-field.v-text-field--solo .v-input__control {
  background-color: var(--v-neutral-base) !important;
  border-radius: 8px !important;
  min-height: 40px !important;
  height: 40px !important;
}

::v-deep .search-field .v-input__slot {
  background-color: var(--v-neutral-base) !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  padding-left: 4px !important;
}

::v-deep .search-field .v-icon {
  background-color: var(--v-primary-base) !important;
  color: #FFFFFF !important;
  border-radius: 8px !important;
  padding: 6px !important;
  width: 32px !important;
  height: 32px !important;
  margin-right: 4px !important;
}

::v-deep .search-field .v-text-field__slot {
  padding-left: 4px !important;
}

::v-deep .search-field input {
  font-size: 13px !important;
}

::v-deep .search-field input::placeholder {
  font-size: 12px !important;
}
</style>

<style>
.amount-input input {
  color: #6D6D6D !important;
  font-size: 12px !important;
  text-align: right !important;
}

.amount-input input[type="number"]::-webkit-outer-spin-button,
.amount-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.amount-input input[type="number"] {
  -moz-appearance: textfield; /* Firefox */
}

/* Remove underline from amount inputs */
.plan-table .amount-input .v-input__control::before,
.plan-table .amount-input .v-input__control::after {
  border: none !important;
}

.plan-table .amount-input .v-text-field__details {
  display: none !important;
}

.plan-table .amount-input.v-text-field > .v-input__control > .v-input__slot:before,
.plan-table .amount-input.v-text-field > .v-input__control > .v-input__slot:after {
  border: none !important;
}

</style>